/**
 * CatastroWMSLayer - Capa WMS de Catastro para Leaflet
 * F7.03 - Overlay de parcelas y edificios del Catastro
 *
 * Fuente: Dirección General del Catastro (INSPIRE WMS)
 * Licencia: CC BY 4.0 compatible - Uso libre
 *
 * PTEL - Sistema de Normalización de Coordenadas
 */

import React, { memo, useCallback } from 'react';
import { WMSTileLayer, useMapEvents } from 'react-leaflet';

// =============================================================================
// CONSTANTES
// =============================================================================

/** URL del servicio WMS del Catastro (INSPIRE) */
export const CATASTRO_WMS_URL =
  'https://ovc.catastro.meh.es/Cartografia/WMS/ServidorWMS.aspx';

/** Capas disponibles en el WMS */
export const CATASTRO_WMS_LAYERS = {
  /** Capa combinada: parcelas + edificios */
  combined: 'Catastro',
  /** Solo subparcelas */
  subparcels: 'SUBPARCE',
  /** Solo construcciones (con altura) */
  buildings: 'CONSTRU',
} as const;

export type CatastroLayerName = keyof typeof CATASTRO_WMS_LAYERS;

/** Atribución oficial del Catastro */
export const CATASTRO_ATTRIBUTION =
  '© <a href="https://www.catastro.meh.es" target="_blank" rel="noopener">DG Catastro</a>';

// =============================================================================
// TIPOS
// =============================================================================

export interface CatastroWMSLayerProps {
  /** Capa visible */
  visible: boolean;
  /** Opacidad de la capa (0-1, default 0.6) */
  opacity?: number;
  /** Tipo de capa a mostrar (default 'combined') */
  layerType?: CatastroLayerName;
  /** Callback en error de carga de tiles */
  onError?: (error: Error) => void;
}

// =============================================================================
// COMPONENTE
// =============================================================================

export const CatastroWMSLayer = memo<CatastroWMSLayerProps>(
  function CatastroWMSLayer({
    visible,
    opacity = 0.6,
    layerType = 'combined',
    onError,
  }) {
    // Gestión de errores de carga
    useMapEvents({
      tileerror: useCallback(
        (event: L.TileErrorEvent) => {
          if (visible && onError) {
            onError(new Error(`Error cargando tile Catastro: ${event.error}`));
          }
        },
        [visible, onError]
      ),
    });

    if (!visible) return null;

    return (
      <WMSTileLayer
        url={CATASTRO_WMS_URL}
        layers={CATASTRO_WMS_LAYERS[layerType]}
        format="image/png"
        transparent={true}
        opacity={opacity}
        attribution={CATASTRO_ATTRIBUTION}
        // WMS params
        version="1.1.1"
        // Zoom limits: Catastro solo tiene detalle a zoom alto
        maxZoom={20}
        minZoom={14}
      />
    );
  }
);

export default CatastroWMSLayer;
